// Daftar rute dipisah dari pembuatan router agar bisa diuji di Node (tanpa
// window/history) — mis. test "setiap menu menunjuk rute terdaftar" di
// src/config/internal-menu.test.ts.
import type { RouteRecordRaw } from 'vue-router'
import type { RoleName } from '@/stores/auth'

export const routes: RouteRecordRaw[] = [
  // --- Portal publik ---
  { path: '/', name: 'publik-beranda', component: () => import('@/pages/publik/Beranda.vue') },
  { path: '/berita', name: 'publik-berita', component: () => import('@/pages/publik/Berita.vue') },
  { path: '/berita/:slug', name: 'publik-berita-detail', component: () => import('@/pages/publik/BeritaDetail.vue') },
  { path: '/agenda', name: 'publik-agenda', component: () => import('@/pages/publik/Jadwal.vue') },
  { path: '/daftar/:kegiatanId', name: 'publik-daftar', component: () => import('@/pages/publik/Daftar.vue') },
  // Kepesertaan (v1.0.0): halaman status peserta & Forum kelasnya.
  { path: '/kepesertaan/forum/:id', name: 'publik-kepesertaan-forum', component: () => import('@/pages/publik/Forum.vue') },
  { path: '/kepesertaan/:id', name: 'publik-kepesertaan', component: () => import('@/pages/publik/PendaftaranStatus.vue') },
  // Alamat lama tetap hidup: tautan /jadwal?cek=1 ada di email sejak v0.17.0.
  // Forum lama berbasis kegiatan dialihkan ke Cek Status — peserta masuk
  // dengan kode pendaftarannya, lalu membuka Forum dari Kepesertaan.
  { path: '/jadwal', redirect: (to) => ({ path: '/agenda', query: to.query, hash: to.hash }) },
  { path: '/pendaftaran/:id', redirect: (to) => ({ path: `/kepesertaan/${String(to.params.id)}`, query: to.query, hash: to.hash }) },
  { path: '/forum/:kegiatanId', redirect: { path: '/agenda', query: { cek: '1' } } },
  { path: '/pendataan', name: 'publik-pendataan', component: () => import('@/pages/publik/PendataanKader.vue') },
  { path: '/cek-data', name: 'publik-cek-data', component: () => import('@/pages/publik/CekData.vue') },
  { path: '/pendukung', name: 'publik-pendukung', component: () => import('@/pages/publik/Pendukung.vue') },
  { path: '/profil', name: 'publik-profil', component: () => import('@/pages/publik/Profil.vue') },
  { path: '/safespace', name: 'publik-safespace', component: () => import('@/pages/publik/SafeSpace.vue') },
  { path: '/panduan', name: 'publik-panduan', component: () => import('@/pages/publik/Panduan.vue') },
  { path: '/surat', name: 'publik-surat', component: () => import('@/pages/publik/Surat.vue') },
  { path: '/surat/:tiket', name: 'publik-surat-lacak', component: () => import('@/pages/publik/SuratLacak.vue') },
  { path: '/bidang/:slug', name: 'publik-bidang-detail', component: () => import('@/pages/publik/BidangDetail.vue') },
  { path: '/verifikasi-syahadah/:nomor_unik?', name: 'publik-verifikasi-syahadah', component: () => import('@/pages/publik/VerifikasiSyahadah.vue') },

  // --- Panel internal ---
  { path: '/internal/login', name: 'internal-login', component: () => import('@/pages/internal/Login.vue') },
  {
    path: '/internal',
    name: 'internal-dashboard',
    component: () => import('@/pages/internal/Dashboard.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/internal/ganti-password',
    name: 'internal-ganti-password',
    component: () => import('@/pages/internal/Keamanan.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/internal/pengajuan',
    name: 'internal-pengajuan',
    component: () => import('@/pages/internal/Pengajuan.vue'),
    meta: { requiresAuth: true, roles: ['PIMP_CABANG', 'KORPS_MADYA', 'BIDANG_KADER', 'SUPER_ADMIN'] as RoleName[] },
  },
  {
    path: '/internal/pengajuan/:id',
    name: 'internal-pengajuan-detail',
    component: () => import('@/pages/internal/PengajuanDetail.vue'),
    meta: { requiresAuth: true, roles: ['PIMP_CABANG', 'KORPS_MADYA', 'BIDANG_KADER'] as RoleName[] },
  },
  {
    path: '/internal/data-kader',
    name: 'internal-data-kader',
    component: () => import('@/pages/internal/DataKaderDPD.vue'),
    meta: { requiresAuth: true, roles: ['BIDANG_KADER', 'KORPS_MADYA', 'SUPER_ADMIN'] as RoleName[] },
  },
  {
    path: '/internal/kegiatan',
    name: 'internal-kegiatan',
    component: () => import('@/pages/internal/Kegiatan.vue'),
    meta: { requiresAuth: true, roles: ['KORPS_MADYA', 'BIDANG_KADER', 'PERANGKAT', 'SUPER_ADMIN'] as RoleName[] },
  },
  {
    // Hub Kegiatan — ringkasan & pintu ke seluruh bagian kerja satu kegiatan.
    path: '/internal/kegiatan/:id',
    name: 'internal-kegiatan-ringkasan',
    component: () => import('@/pages/internal/KegiatanRingkasan.vue'),
    meta: { requiresAuth: true, roles: ['KORPS_MADYA', 'BIDANG_KADER', 'PERANGKAT'] as RoleName[] },
  },
  {
    path: '/internal/kegiatan/:id/pendaftaran',
    name: 'internal-kegiatan-pendaftaran',
    component: () => import('@/pages/internal/KegiatanPendaftaran.vue'),
    // Super Admin: baca-saja, untuk kode pendaftaran (4 Okt 2026).
    meta: { requiresAuth: true, roles: ['KORPS_MADYA', 'BIDANG_KADER', 'PERANGKAT', 'SUPER_ADMIN'] as RoleName[] },
  },
  {
    path: '/internal/kegiatan/:id/kelas',
    name: 'internal-kegiatan-kelas',
    component: () => import('@/pages/internal/KegiatanKelas.vue'),
    meta: { requiresAuth: true, roles: ['KORPS_MADYA', 'BIDANG_KADER', 'PERANGKAT'] as RoleName[] },
  },
  {
    path: '/internal/kegiatan/:id/materi',
    name: 'internal-kegiatan-materi',
    component: () => import('@/pages/internal/KegiatanMateri.vue'),
    meta: { requiresAuth: true, roles: ['KORPS_MADYA', 'BIDANG_KADER', 'PERANGKAT'] as RoleName[] },
  },
  {
    path: '/internal/kegiatan/:id/tes',
    name: 'internal-kegiatan-tes',
    component: () => import('@/pages/internal/KegiatanTes.vue'),
    meta: { requiresAuth: true, roles: ['KORPS_MADYA', 'BIDANG_KADER', 'PERANGKAT'] as RoleName[] },
  },
  {
    path: '/internal/kegiatan/:id/syahadah',
    name: 'internal-kegiatan-syahadah',
    component: () => import('@/pages/internal/KegiatanSyahadah.vue'),
    meta: { requiresAuth: true, roles: ['KORPS_MADYA', 'BIDANG_KADER'] as RoleName[] },
  },
  {
    path: '/internal/safespace',
    name: 'internal-safespace',
    component: () => import('@/pages/internal/SafeSpaceLaporan.vue'),
    meta: { requiresAuth: true, roles: ['PENDAMPING'] as RoleName[] },
  },
  {
    path: '/internal/surat',
    name: 'internal-surat',
    component: () => import('@/pages/internal/Surat.vue'),
    meta: {
      requiresAuth: true,
      roles: ['SEKRETARIAT', 'SUPER_ADMIN', 'PIMP_CABANG', 'KORPS_MADYA', 'BIDANG_KADER'] as RoleName[],
    },
  },
  {
    path: '/internal/profil-organisasi',
    name: 'internal-profil-organisasi',
    component: () => import('@/pages/internal/ProfilOrganisasi.vue'),
    meta: { requiresAuth: true, roles: ['SUPER_ADMIN', 'BIDANG_KADER'] as RoleName[] },
  },
  {
    path: '/internal/pendukung-peserta',
    name: 'internal-pendukung-peserta',
    component: () => import('@/pages/internal/PendukungPeserta.vue'),
    meta: { requiresAuth: true, roles: ['PIMP_CABANG', 'KORPS_MADYA', 'BIDANG_KADER', 'SUPER_ADMIN'] as RoleName[] },
  },
  {
    path: '/internal/kegiatan/:id/peninjauan-rtl',
    name: 'internal-kegiatan-peninjauan-rtl',
    component: () => import('@/pages/internal/KegiatanPeninjauanRTL.vue'),
    meta: { requiresAuth: true, roles: ['PERANGKAT', 'KORPS_MADYA', 'BIDANG_KADER'] as RoleName[] },
  },
  {
    path: '/internal/perlu-tindakan',
    name: 'internal-perlu-tindakan',
    component: () => import('@/pages/internal/PerluTindakan.vue'),
    meta: { requiresAuth: true, roles: ['BIDANG_KADER', 'KORPS_MADYA'] as RoleName[] },
  },
  {
    path: '/internal/syahadah-pengaturan',
    name: 'internal-syahadah-pengaturan',
    component: () => import('@/pages/internal/SyahadahPengaturan.vue'),
    meta: { requiresAuth: true, roles: ['BIDANG_KADER', 'KORPS_MADYA'] as RoleName[] },
  },
  {
    path: '/internal/syahadah-pemutihan',
    name: 'internal-syahadah-pemutihan',
    component: () => import('@/pages/internal/SyahadahPemutihan.vue'),
    meta: { requiresAuth: true, roles: ['BIDANG_KADER', 'KORPS_MADYA'] as RoleName[] },
  },
  {
    path: '/internal/pendaftaran/:id',
    name: 'internal-pendaftaran-review',
    component: () => import('@/pages/internal/PendaftaranReview.vue'),
    meta: { requiresAuth: true, roles: ['KORPS_MADYA', 'BIDANG_KADER', 'PERANGKAT'] as RoleName[] },
  },
  {
    path: '/internal/perubahan-kader',
    name: 'internal-perubahan-kader',
    component: () => import('@/pages/internal/PerubahanKader.vue'),
    meta: { requiresAuth: true, roles: ['PIMP_CABANG'] as RoleName[] },
  },
  {
    path: '/internal/pendataan-kader',
    name: 'internal-pendataan-kader',
    component: () => import('@/pages/internal/PendataanKaderInternal.vue'),
    meta: { requiresAuth: true, roles: ['PIMP_CABANG'] as RoleName[] },
  },
  {
    path: '/internal/manajemen-user',
    name: 'internal-manajemen-user',
    component: () => import('@/pages/internal/ManajemenUser.vue'),
    meta: { requiresAuth: true, roles: ['SUPER_ADMIN'] as RoleName[] },
  },
  {
    path: '/internal/fase-kegiatan',
    name: 'internal-fase-kegiatan',
    component: () => import('@/pages/internal/FaseKegiatan.vue'),
    meta: { requiresAuth: true, roles: ['SUPER_ADMIN'] as RoleName[] },
  },
  {
    path: '/internal/data-kegiatan',
    name: 'internal-data-kegiatan',
    component: () => import('@/pages/internal/DataKegiatan.vue'),
    meta: { requiresAuth: true, roles: ['SUPER_ADMIN'] as RoleName[] },
  },
  {
    path: '/internal/audit-log',
    name: 'internal-audit-log',
    component: () => import('@/pages/internal/AuditLog.vue'),
    meta: { requiresAuth: true, roles: ['SUPER_ADMIN'] as RoleName[] },
  },
  {
    path: '/internal/manajemen-organisasi',
    name: 'internal-manajemen-organisasi',
    component: () => import('@/pages/internal/ManajemenOrganisasi.vue'),
    meta: { requiresAuth: true, roles: ['SUPER_ADMIN'] as RoleName[] },
  },
  {
    path: '/internal/monitoring-agenda',
    name: 'internal-monitoring-agenda',
    component: () => import('@/pages/internal/MonitoringAgenda.vue'),
    meta: { requiresAuth: true, roles: ['KORPS_MADYA', 'BIDANG_KADER'] as RoleName[], fitur: 'monitoring_agenda' },
  },
  {
    path: '/internal/monitoring-agenda/:kegiatanId',
    name: 'internal-monitoring-agenda-detail',
    component: () => import('@/pages/internal/MonitoringAgendaDetail.vue'),
    meta: { requiresAuth: true, roles: ['KORPS_MADYA', 'BIDANG_KADER'] as RoleName[], fitur: 'monitoring_agenda' },
  },
  {
    path: '/internal/kegiatan-saya',
    name: 'internal-kegiatan-saya',
    component: () => import('@/pages/internal/KegiatanSaya.vue'),
    meta: { requiresAuth: true, roles: ['PERANGKAT'] as RoleName[], fitur: 'monitoring_agenda' },
  },
  {
    // Persiapan & Monitoring tidak lagi ditampilkan untuk Perangkat (telaah
    // lanjutan uji putaran 5, T7-2): catatan KIM tampil di Ringkasan. Tautan
    // lama diarahkan ke hub kegiatan.
    path: '/internal/kegiatan-saya/:kegiatanId',
    name: 'internal-kegiatan-saya-detail',
    redirect: (to) => `/internal/kegiatan/${to.params.kegiatanId}`,
  },
  {
    path: '/internal/redaksi',
    name: 'internal-redaksi',
    component: () => import('@/pages/internal/RedaksiBerita.vue'),
    meta: { requiresAuth: true, roles: ['REDAKTUR'] as RoleName[] },
  },

  // --- Fallback ---
  { path: '/:pathMatch(.*)*', name: 'not-found', component: () => import('@/pages/NotFound.vue') },
]
